import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { colors } from '../../config';
import checkmark from 'url:../../assets/checkmark.svg';

// Styles
import {
  wrapperStyle,
  indicatorStyle,
  numberStyle,
  headerStyle,
  contentWrapperStyle,
  contentStyle,
} from './Step.style';

const Step = ({
  activeColor = colors.boldColors.lightBlue.base,
  number,
  header,
  isLast,
  isActive,
  isValid,
  isComplete,
  isWarning,
  children,
  additionalHeight,
  shouldHideInactiveStepsContent,
}) => {
  // Ref to question so that we can scroll it into view if necessary
  const questionRef = useRef(null);
  // Ref to question content wrapper so that we can measure height of content
  const questionContentRef = useRef(null);
  // We need to get the actual height of each question in order to animate properly
  // We update the questionHeight state as soon as the question has been rendered to the DOM
  // and we can measure the height
  const [questionHeight, setQuestionHeight] = useState(0);

  useEffect(() => {
    const el = questionContentRef.current;
    if (!el) return;

    const measure = () => setQuestionHeight(el.offsetHeight);
    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let timer;

    if (isActive) {
      timer = setTimeout(() => {
        const questionElement = questionRef?.current;

        if (typeof questionElement?.scrollIntoView === 'function') {
          questionElement.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
          return;
        }

        if (typeof questionElement?.scrollIntoViewIfNeeded === 'function') {
          questionElement.scrollIntoViewIfNeeded();
        }
      }, 300);
    }

    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <li ref={questionRef} css={wrapperStyle}>
      <div css={indicatorStyle(activeColor, isLast, isValid, isComplete)}>
        <span
          css={numberStyle(
            activeColor,
            isActive,
            isComplete,
            isValid,
            isWarning
          )}
          data-testid="stepNumber"
        >
          {isComplete && !isActive ? (
            <img src={checkmark} alt="Checkmark icon" />
          ) : (
            number
          )}
        </span>
      </div>
      <div css={contentWrapperStyle}>
        <h3 css={headerStyle(activeColor, isActive, isValid, isWarning)}>
          {header}
        </h3>
        <div
          css={
            shouldHideInactiveStepsContent &&
            contentStyle(isActive, questionHeight + additionalHeight)
          }
        >
          <div ref={questionContentRef}>{children}</div>
        </div>
      </div>
    </li>
  );
};

Step.propTypes = {
  activeColor: PropTypes.string,
  number: PropTypes.number,
  header: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
  isActive: PropTypes.bool,
  isValid: PropTypes.bool,
  isComplete: PropTypes.bool,
  isWarning: PropTypes.bool,
  children: PropTypes.node,
  stepId: PropTypes.string.isRequired,
  additionalHeight: PropTypes.number,
  shouldHideInactiveStepsContent: PropTypes.bool,
};

export { Step };
